import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { DataFieldGroup } from '@app/models/settings/data-field-group.model';
import { FieldGroupForm } from '@app/modules/forms/settings/components';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { DataField } from '@models/settings/data-field.model';
import { DefaultFieldNames } from '@settings/enums/default-field-names-enum/default-field-names.enum';
import { FieldsService } from '@settings/services/fields.service';

@Component({
    templateUrl: './field-groups-store.template.html',
    styleUrls: ['./field-groups-store.style.scss'],
})
export class FieldGroupsStoreView implements OnInit {
    isEdit = false;
    isLoading = true;
    displayDefaultFields = false;
    defaultFieldNames: string[] = [];
    fieldGroup: DataFieldGroup | null = null;
    title: string;
    shouldSaveReordering = false;
    displayCustomFields = false;
    customFields: DataField[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private auth: AuthService,
        private analyticsService: AnalyticService,
        private fieldsService: FieldsService
    ) {}

    async ngOnInit(): Promise<void> {
        this.setFieldGroup();
        this.setIsEdit();
        this.setDisplayDefaultFields();
        this.setDefaultFieldNames();
        this.title = this.isEdit ? 'settings.custom-fields.editFieldGroup' : 'settings.custom-fields.createFieldGroup';
        this.setDisplayCustomFields();
        await this.fetchCustomFields();
    }

    /**
     * On edit custom field group information flow, we need to fetch the field.
     */
    setFieldGroup(): void {
        if (!this.route.snapshot.data.models) {
            return;
        }

        this.fieldGroup = this.route.snapshot.data.models.get('fieldGroup');
    }

    setIsEdit(): void {
        this.isEdit = this.fieldGroup?.isPersisted ?? false;
    }

    setDisplayDefaultFields(): void {
        this.displayDefaultFields = this.fieldGroup === null ? false : this.fieldGroup.isDefault;
    }

    setDefaultFieldNames(): void {
        if (!this.displayDefaultFields) {
            return;
        }

        this.defaultFieldNames = Object.values(
            DefaultFieldNames[this.fieldGroup.component][this.fieldGroup.defaultGroupType]
        );
    }

    setDisplayCustomFields(): void {
        this.displayCustomFields = this.isEdit;
    }

    async fetchCustomFields(): Promise<void> {
        if (!this.displayCustomFields) {
            this.isLoading = false;
            return;
        }

        const [fields] = await DataField.param('company', this.fieldGroup.companyId)
            .where('dataFieldGroupIds', [this.fieldGroup.id])
            .orderBy('orderBy', 'ASC')
            .all();
        this.customFields = fields;
        this.isLoading = false;
    }

    drop(event: CdkDragDrop<string[]>): void {
        this.shouldSaveReordering = true;
        moveItemInArray(this.customFields, event.previousIndex, event.currentIndex);
    }

    cancel(event: MouseEvent): void {
        event.preventDefault();
        this.router.navigate(['/settings', 'custom-fields', 'field-groups']);
    }

    async onSave(fieldGroupForm: FieldGroupForm): Promise<void> {
        fieldGroupForm.submit();

        if (fieldGroupForm.form.invalid) {
            return;
        }

        this.isLoading = true;

        try {
            await this.saveFieldGroup();

            if (this.shouldSaveReordering && this.customFields.length > 0) {
                await this.saveFieldsOrdering();
            }

            this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleEditCustomFieldGroup);
            this.router.navigate(['/settings', 'custom-fields', 'field-groups']);
            this.notify.success('settings.custom-fields.customFieldGroupUpdated');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    async saveFieldGroup(): Promise<void> {
        await this.fieldGroup.param('company', this.auth.company.id).save();
    }

    async saveFieldsOrdering(): Promise<void> {
        if (!this.displayCustomFields) {
            return;
        }

        const ids = this.customFields.map((f: DataField) => f.id);
        await this.fieldsService.bulkUpdateOrder(this.fieldGroup.id, ids);
        this.analyticsService.trackEvent(AnalyticEvents.SettingsModuleReorderCustomFields);
    }
}
